/**
 * Helper puri per il flusso di submit dell'OpportunityForm.
 *
 * Estratti da App.js il 25/02/2026 nell'ambito del refactoring del
 * componente monolitico (da 396 righe di `handleSubmit`).
 *
 * Tutte le funzioni qui sono pure (no React, no state, no toast).
 * L'unica funzione async è `uploadAndLinkPhotos` che fa chiamate axios
 * ma riceve i callback (setProgress, setPhase, onError) dall'esterno.
 *
 * Vantaggi:
 *   - Unit-testabili in isolamento
 *   - Riusabili da altri form (es. duplicate-to-indiretti)
 *   - Riducono la cognitive load di handleSubmit
 */

import { format } from "date-fns";

import { REPARTI_CONFIG } from "@/lib/opportunity-constants";

const API = `${window.location.origin}/api`;

/**
 * Risolvi i "name" da inviare al backend usando i lookup attuali, con
 * fallback al prefill (per ID che sono stati rimossi dal listino GHL).
 *
 * Bug fix 04/06/2026: senza questo fallback, salveremmo
 * `assistente_name: ""` perdendo l'informazione storica anche se l'ID
 * resta corretto.
 *
 * @param {object} ids - {medico, assistente, assicurazione}
 * @param {object} lookups - {doctors, assistants, insurances}
 * @param {object} editingPrefill - snapshot per i fallback
 * @returns {{medicoName, assistenteName, assicurazioneName, selectedDoctor}}
 */
export function resolveDisplayNames({ medico, assistente, assicurazione }, { doctors, assistants, insurances }, editingPrefill) {
  const selectedDoctor = doctors.find((d) => d.id === medico);
  const selectedAssistant = assistants.find((a) => a.id === assistente);
  const selectedInsurance = insurances.find((i) => i.id === assicurazione);

  const medicoName = selectedDoctor?.name
    || (medico && editingPrefill?.medico_id === medico ? (editingPrefill.medico_name || "") : "");
  const assistenteName = selectedAssistant?.name
    || (assistente && editingPrefill?.assistente_id === assistente ? (editingPrefill.assistente_name || "") : "");
  const assicurazioneName = selectedInsurance?.name
    || (assicurazione && editingPrefill?.assicurazione_id === assicurazione ? (editingPrefill.assicurazione_name || "") : "");

  return { medicoName, assistenteName, assicurazioneName, selectedDoctor };
}

/**
 * Risolvi il pipelineStageId da inviare a GHL:
 *   - CREATE: usa il default del reparto (o quello scelto dall'utente
 *     se `needsStageSelection`).
 *   - EDIT con stessa pipeline: KEEP lo stage originale (evita
 *     "demote" silenzioso al primo stage).
 *   - EDIT con pipeline diversa: usa il default della nuova pipeline.
 */
export function resolveStageIdToSend({ reparto, stageId, editingOpportunityId, editingPrefill }) {
  const repartoConfig = REPARTI_CONFIG[reparto] || {};
  if (repartoConfig.needsStageSelection) {
    return stageId || "";
  }
  if (
    editingOpportunityId
    && editingPrefill?.original_pipeline_id
    && editingPrefill.original_pipeline_id === repartoConfig.pipelineId
  ) {
    return editingPrefill.original_stage_id || "";
  }
  return repartoConfig.stageId || "";
}

/**
 * Costruisce il payload completo `opportunityData` per POST/PUT al
 * backend GHL. Pure: nessuna chiamata di rete, nessun side effect.
 */
export function buildOpportunityPayload(state, lookups, editingPrefill) {
  const {
    selectedPatient, nome, cognome, telefono, email,
    medico, assistente, assicurazione, reparto,
    stageId, dataPianoCura, pianoCura, isPromemoria, editingOpportunityId,
  } = state;

  const { medicoName, assistenteName, assicurazioneName, selectedDoctor } =
    resolveDisplayNames({ medico, assistente, assicurazione }, lookups, editingPrefill);

  const repartoConfig = REPARTI_CONFIG[reparto] || {};
  const stageIdToSend = resolveStageIdToSend({
    reparto, stageId, editingOpportunityId, editingPrefill,
  });

  // Promemoria reparti hide the date picker, so `dataPianoCura` is
  // always null. To keep these opportunities visible in the archive
  // (which filters/groups by `data_piano_cura`), default to today — il
  // giorno in cui l'opp è creata.
  const dataPianoCuraToSend = dataPianoCura
    ? format(dataPianoCura, "yyyy-MM-dd")
    : (isPromemoria ? format(new Date(), "yyyy-MM-dd") : "");

  return {
    contactId: selectedPatient.id,
    nome, cognome, telefono, email,
    medicoId: medico,
    medicoUserId: selectedDoctor?.userId || medico,
    medicoName,
    assistenteId: assistente || "",
    assistenteName,
    assicurazioneId: assicurazione,
    assicurazioneName,
    pipelineId: repartoConfig.pipelineId || "",
    stageId: stageIdToSend,
    dataPianoCura: dataPianoCuraToSend,
    pianoCura: pianoCura.map((g) => ({
      id: g.id,
      denti: g.denti || [],
      fullMouth: !!g.fullMouth,
      trattamenti: (g.trattamenti || []).map((t) => ({
        treatmentCode: t.treatmentCode,
        treatmentName: t.treatmentName,
        euro: t.euro,
        priceChoice: t.priceChoice || "",
        indirettoTarget: t.indirettoTarget || "",
      })),
    })),
    fotoUrls: [],
  };
}

/**
 * Calcola il valore monetario totale del pianoCura (matching della
 * logica backend in `/ghl/opportunities` CREATE/UPDATE). Usato per il
 * Virtual Duplicate override quando la modifica resta locale.
 */
export function computePianoCuraMonetary(pianoCura) {
  const ALL_TEETH = [18,17,16,15,14,13,12,11,21,22,23,24,25,26,27,28,38,37,36,35,34,33,32,31,41,42,43,44,45,46,47,48];
  try {
    return (pianoCura || []).reduce((sum, g) => {
      const denti = (g.fullMouth ? ALL_TEETH : g.denti) || [];
      return sum + (g.trattamenti || []).reduce(
        (s, t) => s + (Number(t.euro || 0) * denti.length),
        0,
      );
    }, 0);
  } catch {
    return 0;
  }
}

/**
 * Upload N foto in parallelo poi li linka sequenzialmente all'opp via
 * `/ghl/link-photos` (auto-append GHL).
 *
 * I callback (onProgress, onPhase, onToast) sono iniettati dall'esterno
 * così la funzione resta pura rispetto a React/toast.
 *
 * @returns {{linked: number, failedNames: string[], totalUploaded: number}}
 */
export async function uploadAndLinkPhotos({
  photos,
  opportunityId,
  baseProgress = 15, // percent before upload
  uploadShare = 50, // percent of progress dedicated to uploads
  onProgress,
  onPhase,
  onToast,
}) {
  // Lazy import: axios è ESM e non transformabile da Jest out-of-the-box.
  // Importandolo dentro la funzione i pure helper (buildOpportunityPayload,
  // resolveDisplayNames, ecc.) rimangono testabili senza configurazione
  // custom di Jest.
  const { default: axios } = await import("axios");
  if (!photos || photos.length === 0) {
    return { linked: 0, failedNames: [], totalUploaded: 0 };
  }
  onPhase?.(`Caricamento ${photos.length} foto...`);
  let uploadsDone = 0;
  const uploadResults = await Promise.allSettled(
    photos.map((photo) => {
      const fd = new FormData();
      fd.append("files", photo.file, photo.name);
      return axios
        .post(`${API}/ghl/upload-to-storage`, fd, { timeout: 120000 })
        .then((res) => {
          uploadsDone += 1;
          onProgress?.(baseProgress + Math.round((uploadsDone / photos.length) * uploadShare));
          return res;
        });
    }),
  );

  const uploadedUrls = [];
  const failedNames = [];
  uploadResults.forEach((result, i) => {
    const name = photos[i].name || photos[i].file.name;
    if (result.status === "fulfilled" && result.value.data?.url) {
      uploadedUrls.push(result.value.data.url);
    } else {
      const reason = result.status === "rejected"
        ? result.reason?.response?.data?.detail || result.reason?.message
        : "unknown";
      console.error(`Upload failed for ${name}: ${reason}`);
      failedNames.push(name);
    }
  });

  let linked = 0;
  if (uploadedUrls.length > 0) {
    onProgress?.(baseProgress + uploadShare + 5);
    onPhase?.(`Collegamento ${uploadedUrls.length} foto all'opportunità...`);
    try {
      const linkRes = await axios.post(
        `${API}/ghl/link-photos`,
        { opportunityId, urls: uploadedUrls },
        { timeout: 120000 },
      );
      linked = linkRes.data?.linked || 0;
      onProgress?.(baseProgress + uploadShare + 30);
      onToast?.({ type: "success", message: `${linked} foto caricate e collegate` });
    } catch {
      onToast?.({ type: "warning", message: "Foto caricate ma collegamento parziale. Verifica in GHL." });
    }
  } else if (photos.length > 0) {
    onToast?.({ type: "error", message: "Nessuna foto caricata con successo" });
  }
  if (failedNames.length > 0) {
    onToast?.({ type: "error", message: `Foto non caricate: ${failedNames.join(", ")}` });
  }
  return { linked, failedNames, totalUploaded: uploadedUrls.length };
}
